import {FC} from 'react';
import cn from 'classnames';

import {
  COMPLETED_ICON_CLASS_NAME,
  ERROR_ICON_CLASS_NAME,
  ERROR_STATUS_LABEL,
  StepStatus,
} from './constants';
import type {StepperProps} from './types';
import {getStepAriaLabel, getStepKey, getStepStatus} from './utils';

import styles from './styles.module.scss';

const Stepper: FC<StepperProps> = ({
  steps,
  activeStep,
  onChange,
  title,
  subtitle,
  className,
  orientation = 'vertical',
  showLabels = true,
  size = 'md',
  fullWidth = false,
  alwaysClickableStepIndexes,
  disabledStepIndexes,
  completedIconClassName = COMPLETED_ICON_CLASS_NAME,
  errorIconClassName = ERROR_ICON_CLASS_NAME,
  ...rest
}) => {
  const rootClassName = cn(
    styles.stepper,
    {
      [styles.stepper__modHorizontal]: orientation === 'horizontal',
      [styles.stepper__modSizeSm]: size === 'sm',
      [styles.stepper__modNoLabels]: !showLabels,
      [styles.stepper__modFullWidth]: fullWidth,
    },
    className,
  );

  // Set lookups: `includes` inside the map below rescanned the whole array per step.
  const alwaysClickableSteps = new Set(alwaysClickableStepIndexes ?? []);
  const disabledSteps = new Set(disabledStepIndexes ?? []);

  return (
    <div className={rootClassName} {...rest}>
      {(title || subtitle) && (
        <div className={styles.stepper__header}>
          {title && <span className={styles.stepper__title}>{title}</span>}
          {subtitle && <p className={styles.stepper__subtitle}>{subtitle}</p>}
        </div>
      )}
      <ul className={styles.stepper__list}>
        {steps.map((step, index) => {
          const status: StepStatus = getStepStatus(
            index,
            activeStep,
            step.status,
          );
          const isCompleted = status === StepStatus.COMPLETED;
          const isActive = status === StepStatus.ACTIVE;
          const isUpcoming = status === StepStatus.UPCOMING;
          const isError = status === StepStatus.ERROR;
          const isAlwaysClickable = alwaysClickableSteps.has(index);
          const isDisabled = disabledSteps.has(index);
          const isClickable: boolean =
            !!onChange && !isDisabled && (!isActive || isAlwaysClickable);

          const circleClassName = cn(styles.stepper__circle, {
            [styles.stepper__circle_modCompleted]: isCompleted,
            [styles.stepper__circle_modActive]: isActive,
            [styles.stepper__circle_modUpcoming]: isUpcoming,
            [styles.stepper__circle_modError]: isError,
          });

          const labelClassName = cn(styles.stepper__label, {
            [styles.stepper__label_modCompleted]: isCompleted,
            [styles.stepper__label_modActive]: isActive,
            [styles.stepper__label_modUpcoming]: isUpcoming,
            [styles.stepper__label_modError]: isError,
          });

          return (
            <li key={getStepKey(step, index)} className={styles.stepper__item}>
              <button
                type="button"
                disabled={!isClickable}
                aria-label={
                  showLabels ? undefined : getStepAriaLabel(step, isError)
                }
                className={cn(styles.stepper__step, {
                  [styles.stepper__step_modClickable]: isClickable,
                })}
                onClick={() => onChange?.(index)}>
                <div className={circleClassName}>
                  {isCompleted && (
                    <i
                      className={cn(
                        completedIconClassName,
                        styles.stepper__checkIcon,
                      )}
                      aria-hidden="true"
                    />
                  )}
                  {isError && (
                    <i
                      className={cn(
                        errorIconClassName,
                        styles.stepper__errorIcon,
                      )}
                      aria-hidden="true"
                    />
                  )}
                </div>
                {showLabels && (
                  <span className={labelClassName}>{step.label}</span>
                )}
                {/* The error glyph is decorative, so on its own a failed step
                    reads exactly like a normal one. This trails the label so the
                    name comes out as "Canceled Error". The hidden-label mode
                    cannot use it — an aria-label replaces the contents — so
                    getStepAriaLabel folds the same word in there instead. */}
                {showLabels && isError && (
                  <span className={styles.stepper__srOnly}>
                    {ERROR_STATUS_LABEL}
                  </span>
                )}
              </button>
              {index < steps.length - 1 && (
                <div className={styles.stepper__connector} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Stepper;
